import React, { useEffect, useState } from "react";
import { useForm } from 'react-hook-form';
import Swal from 'sweetalert2';

// components
import { Button, Card, CardBody, CardHeader, FormGroup, Form, Input, Row, Col, Spinner } from "reactstrap";
import MaterialTable from 'material-table';
import Select from 'react-select';

// style
import { table_translate, table_style } from "../../constants/MaterialTable.js";

// helpers
import { showMessage } from "../../helpers/message.js";
import { formateDateApi } from "../../helpers/formats.js";

// api
import concessionaria_api from "../../api/concessionaria_api.js";
import contrato_api from "../../api/contrato_api.js";
import painel_api from "../../api/painel_api.js";

export default function PainelFiscalizacao({ history }) {

    const { register, handleSubmit, errors, setValue } = useForm({ mode: "onBlur" });

    const [showLoading, setshowLoading] = useState(false);
    const [list_table, set_list_table] = useState([]);
    const [hasSearched, setHasSearched] = useState(false);
    // Filtro ativo do totalizador: { contrato_Id, tipo: 'formularios' | 'realizadas' | 'pendentes' }
    const [filtroAtivo, setFiltroAtivo] = useState(null);

    // Concessionaria
    const [options_concessionaria, set_options_concessionaria] = useState([]);
    const [concessionaria, set_concessionaria] = useState(null);
    const handleChangeConcessionaria = (value) => {
        set_concessionaria(value);
        setValue("concessionaria", value, true);
        set_contratos([]);
        setValue("contratos", [], true);
        if (value) {
            loadContratos(value.concessionaria_Id);
        } else {
            set_options_contrato([]);
        }
    }

    // Contrato
    const [options_contrato, set_options_contrato] = useState([]);
    const [contratos, set_contratos] = useState([]);
    const handleChangeContrato = (value) => {
        set_contratos(value || []);
        setValue("contratos", value, true);
    }

    const loadContratos = (concessionaria_Id) => {
        contrato_api.getAll(concessionaria_Id).then(result => {
            if (result.status !== 200) {
                showMessage("Aviso", result.message, 'error', null);
                return;
            }
            set_options_contrato(result.data);
        });
    }

    const loadOptionsConcessionarias = () => {
        concessionaria_api.getByUsuarioId().then(result => {
            if (result.status !== 200) {
                showMessage("Aviso", result.message, 'error', null);
                return;
            }
            set_options_concessionaria(result.data);
        });
    }

    // Validation: period no longer than 12 months
    const validatePeriod = (dataInicio, dataFim) => {
        if (!dataInicio || !dataFim) return true;
        const inicio = new Date(dataInicio);
        const fim = new Date(dataFim);
        const limiteMaximo = new Date(inicio);
        limiteMaximo.setMonth(limiteMaximo.getMonth() + 12);
        return fim <= limiteMaximo;
    }

    const onSubmit = (data) => {
        if (!validatePeriod(data.dataInicio, data.dataFim)) {
            Swal.fire({
                title: 'Aviso',
                text: 'O período consultado não pode ser superior a 12 meses.',
                icon: 'warning',
                confirmButtonText: 'OK'
            });
            return;
        }

        const json_submit = {
            inicio: formateDateApi(data.dataInicio),
            fim: formateDateApi(data.dataFim),
            concessionaria_Id: concessionaria ? concessionaria.concessionaria_Id : null,
            contrato_Ids: contratos.map(c => c.contrato_Id),
        }

        setshowLoading(true);
        painel_api.postSituacaoPorContrato(json_submit).then(result => {
            setshowLoading(false);
            if (result.status !== 200) {
                showMessage("Aviso", result.message, 'error', null);
                return;
            }
            set_list_table(result.data);
            setFiltroAtivo(null);
            setHasSearched(true);
        });
    }

    // Alterna o filtro do totalizador (clicar de novo no mesmo indicador limpa)
    const handleFiltroTotalizador = (contrato_Id, tipo) => {
        setFiltroAtivo(prev => {
            if (prev && prev.contrato_Id === contrato_Id && prev.tipo === tipo) {
                return null;
            }
            return { contrato_Id, tipo };
        });
    }

    useEffect(() => {
        loadOptionsConcessionarias();
    }, []);

    // Totalizadores por contrato: cada contrato corresponde a uma linha
    const totalizadoresPorContrato = (() => {
        const mapa = new Map();
        list_table.forEach(r => {
            const chave = r.contrato_Id != null ? r.contrato_Id : r.contrato;
            if (!mapa.has(chave)) {
                mapa.set(chave, {
                    contrato_Id: r.contrato_Id,
                    contrato: r.contrato,
                    concessionaria: r.concessionaria,
                    totalFormularios: 0,
                    realizadas: 0,
                    pendentes: 0,
                });
            }
            const item = mapa.get(chave);
            item.totalFormularios += 1;
            item.realizadas += (r.fiscalizacoesRealizadas || 0);
            item.pendentes += (r.fiscalizacoesPendentes || 0);
        });
        return [...mapa.values()];
    })();

    // Dados da tabela de detalhe respeitando o indicador selecionado no totalizador
    const dados_detalhe = (() => {
        if (!filtroAtivo) return list_table;
        return list_table.filter(r => {
            const chave = r.contrato_Id != null ? r.contrato_Id : r.contrato;
            if (chave !== filtroAtivo.contrato_Id) return false;
            if (filtroAtivo.tipo === 'realizadas') return (r.fiscalizacoesRealizadas || 0) > 0;
            if (filtroAtivo.tipo === 'pendentes') return (r.fiscalizacoesPendentes || 0) > 0;
            return true; // 'formularios' — todos do contrato
        });
    })();

    const isFiltroAtivo = (contrato_Id, tipo) =>
        filtroAtivo && filtroAtivo.contrato_Id === contrato_Id && filtroAtivo.tipo === tipo;

    // Progress bar color logic
    const getProgressColor = (realizadas, pendentes) => {
        if (realizadas > 0 && pendentes === 0) return 'bg-success';
        if (realizadas > 0 && pendentes > 0) return 'bg-warning';
        return 'bg-danger';
    }

    const getProgressPercent = (realizadas, pendentes) => {
        const total = realizadas + pendentes;
        if (total === 0) return 0;
        return Math.round((realizadas / total) * 100);
    }

    return (
        <>
            {showLoading &&
                <div className="carregandoCmp">
                    <div className="box">
                        <Spinner></Spinner>
                    </div>
                </div>
            }
            <div className="content">
                <Row>
                    <Col md="12">
                        <Card className="mt-4">
                            <CardHeader>
                                <h4 className="titleCard">Painel de Situação por Contrato</h4>
                            </CardHeader>
                            <CardBody>
                                <Form onSubmit={handleSubmit(onSubmit)}>
                                    <Row className="mb-4 align-items-end">
                                        <Col lg="3" md="6" sm="12">
                                            <FormGroup>
                                                <label className="form-control-label">Data Início *</label>
                                                <Input
                                                    className="form-control"
                                                    autoComplete="off"
                                                    type="date"
                                                    name="dataInicio"
                                                    innerRef={register({ required: true })}
                                                />
                                                {errors.dataInicio && <span className="form-control-label text-danger">* Campo Obrigatório</span>}
                                            </FormGroup>
                                        </Col>
                                        <Col lg="3" md="6" sm="12">
                                            <FormGroup>
                                                <label className="form-control-label">Data Fim *</label>
                                                <Input
                                                    className="form-control"
                                                    autoComplete="off"
                                                    type="date"
                                                    name="dataFim"
                                                    innerRef={register({ required: true })}
                                                />
                                                {errors.dataFim && <span className="form-control-label text-danger">* Campo Obrigatório</span>}
                                            </FormGroup>
                                        </Col>
                                        <Col lg="3" md="6" sm="12">
                                            <FormGroup>
                                                <label className="form-control-label">Concessionária</label>
                                                <Select
                                                    name="concessionaria"
                                                    options={options_concessionaria}
                                                    getOptionLabel={o => o.identificacao}
                                                    getOptionValue={o => o.concessionaria_Id}
                                                    value={concessionaria}
                                                    onChange={(o) => handleChangeConcessionaria(o)}
                                                    classNamePrefix="select"
                                                    placeholder="[ Todas ]"
                                                    isClearable
                                                    ref={e => register({ name: "concessionaria" }, { required: false })}
                                                />
                                            </FormGroup>
                                        </Col>
                                        <Col lg="3" md="6" sm="12">
                                            <FormGroup>
                                                <label className="form-control-label">Contrato</label>
                                                <Select
                                                    name="contrato"
                                                    options={options_contrato}
                                                    getOptionLabel={o => o.identificacao || o.numero}
                                                    getOptionValue={o => o.contrato_Id}
                                                    value={contratos}
                                                    onChange={(o) => handleChangeContrato(o)}
                                                    isMulti
                                                    classNamePrefix="select"
                                                    placeholder="[ Todos ]"
                                                    isClearable
                                                    isDisabled={!concessionaria}
                                                    ref={e => register({ name: "contratos" }, { required: false })}
                                                />
                                            </FormGroup>
                                        </Col>
                                        <Col lg="12" md="12" sm="12" className="text-center">
                                            <Button color="info" type="submit">
                                                <i className="fas fa-search"></i> Pesquisar
                                            </Button>
                                        </Col>
                                    </Row>
                                </Form>

                                {hasSearched && (
                                    <>
                                        {/* Totalizadores por contrato — cada contrato corresponde a um bloco de cards */}
                                        {totalizadoresPorContrato.map((c, idx) => (
                                            <div key={c.contrato_Id != null ? c.contrato_Id : idx} className="mb-2">
                                                <h5 className="mb-2">
                                                    {c.contrato}
                                                    {c.concessionaria && (
                                                        <small className="text-muted"> — {c.concessionaria}</small>
                                                    )}
                                                </h5>
                                                <Row className="mb-3">
                                                    <Col lg="4" md="4" sm="12" className="mb-3">
                                                        <div
                                                            className={"card border-left h-100" + (isFiltroAtivo(c.contrato_Id, 'formularios') ? ' shadow' : '')}
                                                            style={{ borderLeft: '4px solid #17a2b8', cursor: 'pointer' }}
                                                            onClick={() => handleFiltroTotalizador(c.contrato_Id, 'formularios')}
                                                        >
                                                            <div className="card-body py-3">
                                                                <div className="text-xs font-weight-bold text-info text-uppercase mb-1">
                                                                    Total de Formulários
                                                                </div>
                                                                <div className="h5 mb-0 font-weight-bold text-gray-800">
                                                                    {c.totalFormularios}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </Col>
                                                    <Col lg="4" md="4" sm="12" className="mb-3">
                                                        <div
                                                            className={"card border-left h-100" + (isFiltroAtivo(c.contrato_Id, 'realizadas') ? ' shadow' : '')}
                                                            style={{ borderLeft: '4px solid #28a745', cursor: 'pointer' }}
                                                            onClick={() => handleFiltroTotalizador(c.contrato_Id, 'realizadas')}
                                                        >
                                                            <div className="card-body py-3">
                                                                <div className="text-xs font-weight-bold text-success text-uppercase mb-1">
                                                                    Fiscalizações Realizadas
                                                                </div>
                                                                <div className="h5 mb-0 font-weight-bold text-gray-800">
                                                                    {c.realizadas}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </Col>
                                                    <Col lg="4" md="4" sm="12" className="mb-3">
                                                        <div
                                                            className={"card border-left h-100" + (isFiltroAtivo(c.contrato_Id, 'pendentes') ? ' shadow' : '')}
                                                            style={{ borderLeft: '4px solid #dc3545', cursor: 'pointer' }}
                                                            onClick={() => handleFiltroTotalizador(c.contrato_Id, 'pendentes')}
                                                        >
                                                            <div className="card-body py-3">
                                                                <div className="text-xs font-weight-bold text-danger text-uppercase mb-1">
                                                                    Fiscalizações Pendentes
                                                                </div>
                                                                <div className="h5 mb-0 font-weight-bold text-gray-800">
                                                                    {c.pendentes}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </Col>
                                                </Row>
                                            </div>
                                        ))}
                                        {filtroAtivo && (
                                            <Row className="mb-3">
                                                <Col lg="12" className="text-right">
                                                    <Button color="link" size="sm" onClick={() => setFiltroAtivo(null)}>
                                                        <i className="fas fa-times"></i> Limpar filtro
                                                    </Button>
                                                </Col>
                                            </Row>
                                        )}

                                        {/* Results Table */}
                                        <Row>
                                            <Col lg="12" md="12" sm="12">
                                                <MaterialTable
                                                    style={{ minHeight: "auto" }}
                                                    title="Situação por Contrato"
                                                    columns={[
                                                        {
                                                            title: 'Contrato',
                                                            field: 'contrato'
                                                        },
                                                        {
                                                            title: 'Concessionária',
                                                            field: 'concessionaria'
                                                        },
                                                        {
                                                            title: 'Formulário',
                                                            field: 'formulario'
                                                        },
                                                        {
                                                            title: 'Realizadas',
                                                            field: 'fiscalizacoesRealizadas',
                                                            type: 'numeric'
                                                        },
                                                        {
                                                            title: 'Pendentes',
                                                            field: 'fiscalizacoesPendentes',
                                                            type: 'numeric'
                                                        },
                                                        {
                                                            title: 'Progresso',
                                                            render: (rowData) => {
                                                                const realizadas = rowData.fiscalizacoesRealizadas || 0;
                                                                const pendentes = rowData.fiscalizacoesPendentes || 0;
                                                                const percent = getProgressPercent(realizadas, pendentes);
                                                                const colorClass = getProgressColor(realizadas, pendentes);
                                                                return (
                                                                    <div style={{ minWidth: '120px' }}>
                                                                        <div className="progress" style={{ height: '8px', marginBottom: '4px' }}>
                                                                            <div
                                                                                className={"progress-bar " + colorClass}
                                                                                role="progressbar"
                                                                                style={{ width: percent + '%' }}
                                                                                aria-valuenow={percent}
                                                                                aria-valuemin="0"
                                                                                aria-valuemax="100"
                                                                            ></div>
                                                                        </div>
                                                                        <small className="text-muted">{percent}%</small>
                                                                    </div>
                                                                );
                                                            }
                                                        },
                                                        {
                                                            title: 'Situação',
                                                            render: (rowData) => {
                                                                const realizadas = rowData.fiscalizacoesRealizadas || 0;
                                                                const pendentes = rowData.fiscalizacoesPendentes || 0;
                                                                if (realizadas > 0 && pendentes === 0) {
                                                                    return <span className="badge badge-success">Concluído</span>;
                                                                }
                                                                if (realizadas > 0 && pendentes > 0) {
                                                                    return <span className="badge badge-warning">Em Andamento</span>;
                                                                }
                                                                return <span className="badge badge-danger">Pendente</span>;
                                                            }
                                                        },
                                                    ]}
                                                    data={dados_detalhe}
                                                    isLoading={false}
                                                    options={{
                                                        ...table_style,
                                                        filtering: false,
                                                        exportButton: true
                                                    }}
                                                    localization={table_translate}
                                                />
                                            </Col>
                                        </Row>

                                        {/* Legend */}
                                        <Row className="mt-4 text-center">
                                            <Col lg="4" md="4" sm="4">
                                                <i className="fas fa-circle text-success"></i>
                                                <strong className="pl-2">Concluído</strong>
                                                <small className="d-block text-muted">Todas realizadas</small>
                                            </Col>
                                            <Col lg="4" md="4" sm="4">
                                                <i className="fas fa-circle text-warning"></i>
                                                <strong className="pl-2">Em Andamento</strong>
                                                <small className="d-block text-muted">Parcialmente realizado</small>
                                            </Col>
                                            <Col lg="4" md="4" sm="4">
                                                <i className="fas fa-circle text-danger"></i>
                                                <strong className="pl-2">Pendente</strong>
                                                <small className="d-block text-muted">Nenhuma realizada</small>
                                            </Col>
                                        </Row>
                                    </>
                                )}
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            </div>
        </>
    );
}
